import { AnimatePresence, motion } from 'framer-motion';
import { Dialog } from '@headlessui/react';
import classNames from 'classnames';
import React, {
  CSSProperties,
  ReactNode,
  Ref,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useSidebarPresentation } from './useSidebarPresentation';
import styles from './Sidebar.module.scss';
import {
  Button,
  Heading,
  Icon,
  IconList,
  ToastAnnouncement,
} from 'components/core';

// Positional sub-components, like ModalHeader / ModalBody / ModalFooter: a
// panel renders SidebarActions (optional), SidebarBody, then SidebarFooter
// (optional) as the Sidebar's children.
export const SidebarActions = ({ children }: { children: ReactNode }) => (
  <div className={styles.PanelActions}>{children}</div>
);

export const SidebarBody = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div className={classNames(styles.PanelContent, className)}>{children}</div>
);

export const SidebarFooter = ({ children }: { children: ReactNode }) => (
  <div className={styles.PanelFooter}>{children}</div>
);

export type SidebarProps = {
  children: ReactNode;
  closeButtonLabel: string;
  disclosureId: string;
  headingId: string;
  isOpen: boolean;
  onClose: () => void;
  onClosed?: () => void;
  panelRoot: HTMLElement;
  panelRef?: Ref<HTMLElement>;
  panelStyle?: CSSProperties;
  title: ReactNode;
  isCloseDisabled?: boolean;
  hasHeaderDivider?: boolean;
};

// Presents its children either docked beside the page content or, on narrow viewports, as a modal sheet.
export const Sidebar = ({
  children,
  closeButtonLabel,
  disclosureId,
  headingId,
  isOpen,
  onClose,
  onClosed,
  panelRoot,
  panelRef,
  panelStyle,
  title,
  isCloseDisabled,
  hasHeaderDivider = false,
}: SidebarProps) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const presentation = useSidebarPresentation();
  const isDialog = presentation === 'dialog';
  const previousPresentation = useRef(presentation);

  useLayoutEffect(() => {
    if (
      isOpen &&
      previousPresentation.current !== presentation &&
      closeButtonRef.current
    ) {
      window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    }

    previousPresentation.current = presentation;
  }, [isOpen, presentation]);

  useEffect(() => {
    if (isOpen && isDialog) {
      document.body.classList.add(styles.DialogOpen);
    } else if (!isDialog) {
      document.body.classList.remove(styles.DialogOpen);
    }
  }, [isOpen, isDialog]);

  useEffect(() => {
    return () => document.body.classList.remove(styles.DialogOpen);
  }, []);

  const handleDialogExitComplete = () => {
    if (!isOpen) {
      document.body.classList.remove(styles.DialogOpen);
    }
  };

  const heading = (
    <Heading
      component="h2"
      type="h4"
      id={headingId}
      tabIndex={-1}
      className={styles.PanelTitle}
    >
      {title}
    </Heading>
  );

  const headerClasses = classNames({
    [styles.PanelHeader]: true,
    // Opt-in until the criteria sidebar moves to the new design, then every header gets it
    [styles.HeaderWithDivider]: hasHeaderDivider,
  });

  const panelHeader = (
    <div className={headerClasses}>
      {isDialog ? <Dialog.Title as="div">{heading}</Dialog.Title> : heading}
      <Button
        ref={closeButtonRef}
        iconOnly
        size="xs"
        type="neutral"
        variant="ghost"
        aria-label={closeButtonLabel}
        disabled={isCloseDisabled}
        onClick={onClose}
      >
        <Icon icon={IconList.light.faXmark} size="xl" />
      </Button>
    </div>
  );

  const slideTransition = {
    initial: { x: '100%' },
    animate: { x: 0 },
    exit: { x: '100%' },
    transition: { duration: 0.2, ease: 'easeInOut' },
  } as const;

  if (isDialog) {
    return (
      <AnimatePresence
        initial={false}
        onExitComplete={handleDialogExitComplete}
      >
        {isOpen && (
          <Dialog static open={true} onClose={onClose}>
            <motion.div
              aria-hidden="true"
              className={styles.DialogOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
            />
            <Dialog.Panel className={styles.DialogPanel}>
              <motion.div
                id={disclosureId}
                className={styles.DialogSurface}
                {...slideTransition}
              >
                {panelHeader}
                {children}
                <ToastAnnouncement />
              </motion.div>
            </Dialog.Panel>
          </Dialog>
        )}
      </AnimatePresence>
    );
  }

  if (!panelRoot) return null;

  return createPortal(
    <AnimatePresence initial={false} onExitComplete={onClosed}>
      {isOpen && (
        <motion.aside
          ref={panelRef}
          id={disclosureId}
          aria-labelledby={headingId}
          className={styles.Panel}
          style={panelStyle}
          {...slideTransition}
        >
          {panelHeader}
          {children}
        </motion.aside>
      )}
    </AnimatePresence>,
    panelRoot
  );
};
